import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ToasterContainer,
  ToasterMainContent,
  ToasterIconSection,
  ToasterContentSection,
  ToasterContent,
  ToasterTitle,
  ToasterMessage,
  ToasterActionsContainer,
  ToasterActionButton,
  ToasterLink,
  ToasterCloseButton,
  ProgressBarContainer,
  ProgressBar,
} from './StyledComponents';

const Toaster = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'success',
  duration = 5000,
  action,
  link,
  persistent = false,
}) => {
  const [progress, setProgress] = useState(100);

  // Auto-dismiss logic
  useEffect(() => {
    if (isOpen && duration && !action && !link) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isOpen, duration, onClose, action, link]);

  // Progress bar animation for persistent toasters
  useEffect(() => {
    if (!isOpen || !persistent || !duration) {
      setProgress(100);
      return;
    }

    const startTime = Date.now();
    setProgress(100);

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, duration - elapsed);
      const newProgress = (remaining / duration) * 100;

      setProgress(newProgress);

      if (remaining === 0) {
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [isOpen, persistent, duration]);

  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'error':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case 'info':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      default:
        return null;
    }
  };

  const getActionIcon = () => {
    if (action?.icon === 'undo') {
      return (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
          />
        </svg>
      );
    }
    if (action?.icon === 'retry') {
      return (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
          />
        </svg>
      );
    }
    return null;
  };

  const getLinkIcon = () => {
    if (link?.icon === 'external') {
      return (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
          />
        </svg>
      );
    }
    if (link?.icon === 'credit-card') {
      return (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
          />
        </svg>
      );
    }
    if (link?.icon === 'document') {
      return (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      );
    }
    return null;
  };

  const hasAction = action || link;

  return ReactDOM.createPortal(
    <ToasterContainer type={type}>
      <ToasterMainContent>
        <ToasterIconSection type={type}>{getIcon()}</ToasterIconSection>
        <ToasterContentSection hasAction={hasAction}>
          <ToasterContent>
            <ToasterTitle>{title}</ToasterTitle>
            {message && (
              <ToasterMessage hasLink={!!link}>{message}</ToasterMessage>
            )}
            {link && (
              <ToasterLink
                href={link.href}
                onClick={link.onClick}
                type={type}
                target={link.target || '_self'}
              >
                {link.text}
                {getLinkIcon()}
              </ToasterLink>
            )}
          </ToasterContent>
          <ToasterActionsContainer>
            {action && (
              <ToasterActionButton type={type} onClick={action.onClick}>
                {getActionIcon()}
                {action.text}
              </ToasterActionButton>
            )}
            <ToasterCloseButton onClick={onClose}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </ToasterCloseButton>
          </ToasterActionsContainer>
        </ToasterContentSection>
      </ToasterMainContent>
      {persistent && (
        <ProgressBarContainer>
          <ProgressBar type={type} style={{ width: `${progress}%` }} />
        </ProgressBarContainer>
      )}
    </ToasterContainer>,
    document.body
  );
};

Toaster.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  message: PropTypes.string,
  type: PropTypes.oneOf(['success', 'error', 'info']),
  duration: PropTypes.number,
  action: PropTypes.shape({
    text: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.oneOf(['undo', 'retry']),
  }),
  link: PropTypes.shape({
    text: PropTypes.string.isRequired,
    href: PropTypes.string,
    onClick: PropTypes.func,
    target: PropTypes.string,
    icon: PropTypes.oneOf(['external', 'credit-card', 'document']),
  }),
  persistent: PropTypes.bool,
};

export default Toaster;
